import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

const BUCKET = 'fichas';
const VALIDADE_SEGUNDOS = 3600;
const RENOVAR_ANTES_MS = 5 * 60 * 1000;
const VERIFICAR_A_CADA_MS = 60 * 1000;

// Fotos de rosto da ficha de inscrição (tipo 'foto' na vw_respostas_inscricao),
// o mesmo caminho comprovado da montagem de equipes. A foto do cadastro
// (conta Google) nunca entra. Devolve um mapa inscricao_id -> URL assinada.
// Se qualquer passo falhar, devolve vazio em silêncio: a linha mostra iniciais.
export function useFotosDaFicha(ids: number[]): Record<number, string> {
  const [fotos, setFotos] = useState<Record<number, string>>({});

  const caminhosRef = useRef<Record<number, string>>({});
  const expiraEmRef = useRef<number | null>(null);

  // Chave estável: evita recarregar a cada render por causa da nova array.
  const chave = [...ids].sort((a, b) => a - b).join(',');

  // Assina TODOS os caminhos de uma vez só (uma chamada para todos).
  const assinar = useCallback(async (caminhoPorInscricao: Record<number, string>) => {
    const caminhos = Array.from(new Set(Object.values(caminhoPorInscricao)));
    if (caminhos.length === 0) return;
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(caminhos, VALIDADE_SEGUNDOS);
      if (error) return;
      const porCaminho: Record<string, string> = {};
      (data ?? []).forEach((item) => {
        if (item?.signedUrl && item.path) porCaminho[item.path] = item.signedUrl;
      });
      const novo: Record<number, string> = {};
      Object.entries(caminhoPorInscricao).forEach(([id, caminho]) => {
        const url = porCaminho[caminho];
        if (url) novo[Number(id)] = url;
      });
      setFotos(novo);
      expiraEmRef.current = Date.now() + VALIDADE_SEGUNDOS * 1000;
    } catch {
      // Fotos são opcionais: se falhar, a linha fica com as iniciais.
    }
  }, []);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const lista = chave
        ? chave
            .split(',')
            .map(Number)
            .filter((n) => Number.isFinite(n))
        : [];
      if (lista.length === 0) {
        caminhosRef.current = {};
        setFotos({});
        return;
      }
      try {
        const { data, error } = await supabase
          .from('vw_respostas_inscricao')
          .select('inscricao_id, resposta_texto')
          .eq('tipo', 'foto')
          .in('inscricao_id', lista);
        if (error) return;

        const linhas =
          (data as { inscricao_id: number; resposta_texto: string | null }[] | null) ?? [];
        const caminhoPorInscricao: Record<number, string> = {};
        linhas.forEach((l) => {
          if (l.resposta_texto && !caminhoPorInscricao[l.inscricao_id]) {
            caminhoPorInscricao[l.inscricao_id] = l.resposta_texto;
          }
        });
        if (!ativo) return;
        caminhosRef.current = caminhoPorInscricao;
        await assinar(caminhoPorInscricao);
      } catch {
        // Silencioso: as iniciais de hoje continuam valendo.
      }
    })();
    return () => {
      ativo = false;
    };
  }, [chave, assinar]);

  // Perto do vencimento (1 h), re-assina sozinho, sem recarregar a tela.
  useEffect(() => {
    const t = setInterval(() => {
      if (expiraEmRef.current && Date.now() > expiraEmRef.current - RENOVAR_ANTES_MS) {
        assinar(caminhosRef.current);
      }
    }, VERIFICAR_A_CADA_MS);
    return () => clearInterval(t);
  }, [assinar]);

  return fotos;
}